import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';

import { replaceParams } from '@evc/web-components';

import { PlatformConfigService } from '../../../features/config/config.service';
import { STORAGE_API_ENDPOINTS } from '../../../features/config/config.type';
import { ApiResponse } from '../../../features/types/api.type';
import { FILE_DOCUMENT_TYPE, FILE_ICON, FileIcon } from './file.enum';
import {
  StorageApiBrowsePayload,
  StorageApiBrowseResponse,
  StorageApiCopyPayload,
  StorageApiCreateFolderPayload,
  StorageApiEmptyTrashResponse,
  StorageApiMovePayload,
  StorageApiRenamePayload,
  StorageApiTrashRestoreResponse,
  StorageDataSort,
  StorageFile,
  StorageFileList,
  StorageIconFile,
  StorageTrashRestore,
} from './file.type';
import { getFileIconFromMimeType } from './file-icon';

const sortMapping: Record<string, string> = {
  name: 'sortByName',
  createdAt: 'sortByCreatedDate',
  updatedAt: 'sortByUpdatedDate',
  size: 'sortBySize',
};

@Injectable({
  providedIn: 'root',
})
export class FileService {
  readonly #configService = inject(PlatformConfigService);
  readonly #httpClient = inject(HttpClient);
  readonly #destroyRef = inject(DestroyRef);

  /** Browses files and directories based on the provided payload and sorting options.
   * @param payload The payload containing browsing options.
   * @param sort Helper to set the sorting options. It doesn't override the payload sorting options.
   */
  browse$(payload: StorageApiBrowsePayload, sort?: StorageDataSort): Observable<StorageFileList> {
    const params = new URLSearchParams();

    if (sort?.active && sort?.direction) {
      const sortProperty = sortMapping[sort.active];
      if (sortProperty) {
        params.set(sortProperty, sort.direction === 'asc' ? 'true' : 'false');
      }
    }

    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined && value !== null && (typeof value !== 'string' || value.length)) {
        params.set(key, value.toString());
      }
    }

    return this.#configService.safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.browse).pipe(
      switchMap((uri) => this.#httpClient.get<StorageApiBrowseResponse>(`${uri}?${params}`)),
      map(({ result }) => ({
        ...result,
        count: result.totalCount,
        items: result.items.map((file) => ({ ...file, icon: this.getFileIcon(file) })),
      })),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Retrieves a single file or folder by its ID.
   * @param fileId The ID of the file or folder to retrieve.
   */
  getFile$(fileId: string): Observable<StorageIconFile> {
    return this.#configService.safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.file).pipe(
      switchMap((uri) =>
        this.#httpClient.get<ApiResponse<StorageFile>>(replaceParams(uri, { fileId })),
      ),
      map(({ result }) => ({
        ...result,
        icon: this.getFileIcon(result),
      })),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Moves a file or folder to the trash (soft delete).
   * @param fileId The ID of the file or folder to soft-delete.
   */
  softDeleteFile$(fileId: string): Observable<StorageIconFile> {
    return this.#configService.safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.file).pipe(
      switchMap((uri) =>
        this.#httpClient.delete<ApiResponse<StorageFile>>(replaceParams(uri, { fileId })),
      ),
      map(({ result }) => ({
        ...result,
        icon: this.getFileIcon(result),
      })),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Moves a file or folder to a different parent directory.
   * @param payload Contains the file ID and destination parent directory ID.
   */
  moveFile$(payload: StorageApiMovePayload): Observable<StorageIconFile> {
    return this.#configService.safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.move).pipe(
      switchMap((uri) => this.#httpClient.put<ApiResponse<StorageFile>>(uri, payload)),
      map(({ result }) => ({
        ...result,
        icon: this.getFileIcon(result),
      })),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Renames a file or folder.
   * @param payload Contains the file ID and the new name.
   */
  renameFile$(payload: StorageApiRenamePayload): Observable<StorageIconFile> {
    return this.#configService.safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.rename).pipe(
      switchMap((uri) => this.#httpClient.put<ApiResponse<StorageFile>>(uri, payload)),
      map(({ result }) => ({
        ...result,
        icon: this.getFileIcon(result),
      })),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Copies a file to a destination directory.
   * @param payload Contains the source file ID and the destination parent directory ID.
   */
  copyFile$(payload: StorageApiCopyPayload): Observable<StorageIconFile> {
    return this.#configService.safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.copy).pipe(
      switchMap((uri) => this.#httpClient.post<ApiResponse<StorageFile>>(uri, payload)),
      map(({ result }) => ({
        ...result,
        icon: this.getFileIcon(result),
      })),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Creates a new folder.
   * @param payload Contains the folder name and the parent directory ID.
   */
  createFolder$(payload: StorageApiCreateFolderPayload): Observable<StorageIconFile> {
    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.directory)
      .pipe(
        switchMap((uri) => this.#httpClient.post<ApiResponse<StorageFile>>(uri, payload)),
        map(({ result }) => ({
          ...result,
          icon: this.getFileIcon(result),
        })),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /** Restores a file or folder from the trash to its original location.
   * @param fileId The ID of the trashed file or folder to restore.
   */
  restoreFile$(fileId: string): Observable<StorageTrashRestore> {
    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.trash.restore)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.put<StorageApiTrashRestoreResponse>(
            replaceParams(uri, { fileId }),
            null,
          ),
        ),
        map(({ result }) => ({
          ...result,
          file: {
            ...result.file,
            icon: this.getFileIcon(result.file),
          },
        })),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /** Permanently deletes a file or folder from the trash. This action cannot be undone.
   * @param fileId The ID of the trashed file or folder to permanently delete.
   */
  permanentlyDeleteFile$(fileId: string): Observable<StorageIconFile> {
    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.trash.permanent)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.delete<ApiResponse<StorageFile>>(replaceParams(uri, { fileId })),
        ),
        map(({ result }) => ({
          ...result,
          icon: this.getFileIcon(result),
        })),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /** Permanently deletes all items currently in the trash. This action cannot be undone. */
  emptyTrash$(): Observable<StorageApiEmptyTrashResponse['result']> {
    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.files.trash.empty)
      .pipe(
        switchMap((uri) => this.#httpClient.delete<StorageApiEmptyTrashResponse>(uri)),
        map(({ result }) => result),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /**
   * Gets the right icon for a given file based on its type and content type.
   * @param file The file for which to determine the appropriate icon.
   * @returns The icon representing the file type.
   */
  getFileIcon(file: StorageFile): FileIcon {
    if (file.type === FILE_DOCUMENT_TYPE.FOLDER) return FILE_ICON.FOLDER;

    return getFileIconFromMimeType(file.latestVersion?.contentType);
  }
}
